import get from 'lodash/get';
import PropTypes from 'prop-types';
import React from 'react';
import { Provider, useQuery } from 'urql';

import * as BackstageConstants from '../../../constants';
import * as D from '../../../deps';
import { useClickControls } from '../../common/ArtistButtonControls';
import LayoutManager from '../../layout/LayoutManager';

import BackstageArtistContent from './BackstageArtistContent';

// @ts-expect-error TS(7006) FIXME: Parameter 'props' implicitly has an 'any' type.
const BackstageArtistDataFetcher = props => {
  const {
    currentlyPlayingPandoraId,
    initialLoadAnnotation,
    isAudioPlaying,
    openServiceErrorToast,
    pandoraId,
    defaultArtUrl,
  } = props;

  const [{ data, fetching, error }] = useQuery({
    query: D.GraphQL.Queries.getArtistDetailsWithCurators,
    variables: {
      pandoraId,
    },
  });
  const topTracks = get(data, 'entity.topTracksWithCollaborations', []);

  const renderedTopTracks = topTracks.slice(
    0,
    BackstageConstants.BackstageConstants.NUM_TOP_TRACKS_TO_RENDER,
  );

  // @ts-expect-error TS(7006) FIXME: Parameter 'topSong' implicitly has an 'any' type.
  const hasOnDemandTopSongs = renderedTopTracks.some(topSong => {
    return get(topSong, 'rightsInfo.hasInteractive', false);
  });

  const artistPlayId = get(data, 'entity.artistPlay.id', null);
  const hasRadio = get(data, 'entity.canSeedStation', false);
  const urlPath = get(data, 'entity.urlPath', null);

  const { playArtistStation, playTopSongs, pauseTopSongs, shareArtist } = useClickControls({
    artistPlayId,
    closeModal: props.closeModal,
    hasRadio,
    isArtistStationPlaying: props.isArtistStationPlaying,
    nowPlayingUrl: props.nowPlayingUrl,
    openModal: props.openModal,
    openShareModal: props.openShareModal,
    pandoraId,
    pauseTrack: props.pauseTrack,
    playResumeSource: props.playResumeSource,
    shareableUrlPath: urlPath,
    startStation: props.startStation,
    topSongsHaveNoRights: !hasOnDemandTopSongs,
    topSongsLooksDisabled: !hasOnDemandTopSongs,
  });

  const commonProps = {
    children: props.children,
    isArtistTopSongsPlaying: isAudioPlaying && currentlyPlayingPandoraId === artistPlayId,
    isLoaded: !error && !fetching && !!data,
    isMobileWeb: props.isMobileWeb,
    isRightRailActive: props.isRightRailActive,
    pandoraId,
    pauseTopSongs,
    playArtistStation,
    playTopSongs,
    shareArtist,
  };

  const curatedStations = get(data, 'entity.curator.curatedStations.items', []);
  const curatedPlaylists = get(data, 'entity.curator.playlists.items', []);

  const artistGraphqlProps = {
    ...commonProps,
    albumCount: get(data, 'entity.allArtistAlbums.totalItems', 0),
    artistEvents: get(data, 'entity.events', []),
    artistPlayId,
    artistTracksId: get(data, 'entity.artistTracksId', ''),
    bio: get(data, 'entity.bio', ''),
    curatedPlaylists,
    curatedStations,
    artUrl: get(data, 'entity.art.artUrl', '') || defaultArtUrl,
    featuredContent: get(data, 'entity.featured', []),
    hasCuratorPrograms: curatedStations.length !== 0 || curatedPlaylists.length !== 0,
    hasRadio,
    headerArtUrl: get(data, 'entity.headerArt.artUrl', ''),
    isMegastar: get(data, 'entity.isMegastar', false),
    largeHeaderDominantColor: get(data, 'entity.headerArt.dominantColor', ''),
    largeHeaderUrl: get(data, 'entity.headerArt.artUrl', ''),
    latestRelease: get(data, 'entity.latestReleaseWithCollaborations', {}),
    latestReleaseArtUrl: get(data, 'entity.latestReleaseWithCollaborations.icon.artUrl', ''),
    name: get(data, 'entity.name', ''),
    pandoraId: props.pandoraId,
    similarArtists: get(data, 'entity.similarArtists', []),
    stationListenerCount: get(data, 'entity.stationListenerCount', 0),
    topAlbums: get(data, 'entity.topAlbumsWithCollaborations', []),
    topSongsHaveNoRights: !hasOnDemandTopSongs,
    topTracks,
    twitterUrl: get(data, 'entity.twitterUrl', ''),
    urlPath,
  };

  const artistAnnotationProps = {
    artistName: get(initialLoadAnnotation, 'name', ''),
    hasRadio: get(initialLoadAnnotation, 'hasRadio', false),
    heroImageUrl: get(initialLoadAnnotation, 'icon.artUrl', ''),
    ...commonProps,
  };

  if (error) {
    openServiceErrorToast();
    return <LayoutManager pandoraId={pandoraId} />;
  }

  if (fetching) {
    // on initial app load show the sidebar from store data while graphql data fetches
    if (initialLoadAnnotation) {
      return <BackstageArtistContent {...artistAnnotationProps} />;
    }
    return <BackstageArtistContent pandoraId={pandoraId} />;
  }

  return <BackstageArtistContent {...artistGraphqlProps} />;
};

// @ts-expect-error TS(7006) FIXME: Parameter 'props' implicitly has an 'any' type.
export const BackstageArtistProvider = props => {
  return (
    <Provider value={D.GraphQL.UrqlClient}>
      <BackstageArtistDataFetcher {...props} />
    </Provider>
  );
};

BackstageArtistDataFetcher.propTypes = {
  children: PropTypes.element,
  closeModal: PropTypes.func,
  currentlyPlayingPandoraId: PropTypes.string,
  defaultArtUrl: PropTypes.string,
  initialLoadAnnotation: PropTypes.object,
  isArtistStationPlaying: PropTypes.bool,
  isAudioPlaying: PropTypes.bool,
  isCuratorExperienceEnabled: PropTypes.bool,
  isMobileWeb: PropTypes.bool,
  isRightRailActive: PropTypes.bool,
  nowPlayingUrl: PropTypes.string,
  openModal: PropTypes.func,
  openServiceErrorToast: PropTypes.func,
  openShareModal: PropTypes.func,
  pandoraId: PropTypes.string,
  pauseTrack: PropTypes.func,
  playResumeSource: PropTypes.func,
  startStation: PropTypes.func,
};

export default BackstageArtistProvider;
